import React, { createContext, useContext } from 'react';
import classNames from 'classnames';
import { ModalPortal } from '../../../components';
import css from '../UploadImageWizard.css';
import { useUploadImageWizardContext } from '../UploadImageWizard';
import {
    STEP_UPLOAD_PIC_EDIT,
    STEP_UPLOAD_PIC_LIST,
    STEP_UPLOAD_PIC_SELECTED,
    UPLOAD_TYPE_CAMERA_PHOTO,
    UPLOAD_TYPE_DEFAULT_PIC,
    UPLOAD_TYPE_FB_PIC,
} from '../helpers';
import TakeCameraPhoto from '../strategies/TakeCameraPhoto';
import UploadFromFB from '../strategies/UploadFromFB';
import UploadDefaultPic from '../strategies/UploadDefaultPic';

const StrategySelectedContext = createContext(undefined);

const useStrategySelectedContext = () => {
    const context = useContext(StrategySelectedContext);

    if (!context) {
        throw new Error('useStrategySelectedContext must be used within StrategySelectedContext.');
    }

    return context;
};

export const StrategySelected = ({ children }) => {
    const {
        uploadState,
        setUploadState,
        strategy,
        setStrategy,
        setSrc,
    } = useUploadImageWizardContext();

    const goToEdit = () => setUploadState(STEP_UPLOAD_PIC_EDIT);

    const defaultHandler = src => {
        setSrc(src);
        goToEdit();
    };
    const onClose = () => {
        setUploadState(STEP_UPLOAD_PIC_LIST);
        setStrategy(null);
    };

    const isSelectedState = uploadState === STEP_UPLOAD_PIC_SELECTED;
    const isPhotoStrategy = strategy === UPLOAD_TYPE_CAMERA_PHOTO;

    if (!isSelectedState) return null;
    if (!strategy) return null;

    return (
        <StrategySelectedContext.Provider value={{ defaultHandler }}>
            <ModalPortal
                id="UploadImageStrategyModal"
                isOpen
                isSticky={!isPhotoStrategy}
                onClose={onClose}
                contentClassName={classNames(css.modalContent, css.strategyModalContent, {
                    [css[strategy]]: !!css[strategy],
                })}
            >
                {children}
            </ModalPortal>
        </StrategySelectedContext.Provider>
    );
};

const Pic = ({ onImageUpload, children, strategyId }) => {
    const { defaultHandler } = useStrategySelectedContext();
    const wizardContext = useUploadImageWizardContext();
    const { strategy } = wizardContext;

    if (strategyId !== strategy) return null;

    return React.Children.map(children, child => {
        return React.cloneElement(child, {
            onImageUpload:
                typeof onImageUpload === 'function'
                    ? canvasData => onImageUpload(canvasData, wizardContext)
                    : defaultHandler,
        });
    });
};

const CameraPhoto = ({ convertTo, ...rest }) => (
    <Pic {...rest} strategyId={UPLOAD_TYPE_CAMERA_PHOTO}>
        <TakeCameraPhoto convertTo={convertTo} />
    </Pic>
);

const Facebook = ({ isMultiple, inputProps, ...rest }) => (
    <Pic {...rest} strategyId={UPLOAD_TYPE_FB_PIC}>
        <UploadFromFB isMultiple={isMultiple} inputProps={inputProps} />
    </Pic>
);

const Default = props => {
    const { setUploadState, setStrategy } = useUploadImageWizardContext();
    const { strategy } = useUploadImageWizardContext();

    if (UPLOAD_TYPE_DEFAULT_PIC !== strategy) return null;

    const interrupt = () => {
        setUploadState(null);
        setStrategy(null);
    };

    return (
        <UploadDefaultPic
            upload={async formData => {
                await props.onImageUpload(formData.get('file'));
                interrupt();
            }}
        />
    );
};

StrategySelected.CameraPhoto = CameraPhoto;
StrategySelected.Facebook = Facebook;
StrategySelected.Default = Default;
